import {
  animateUserMemory,
  deleteUserMemory,
  getReserveTotal,
  useUserMemory,
  useUserMemoryAnimationReserve,
} from 'api/goodtrust/user/memory'
import MemoryTitle from 'components/content/memory/memoryTitle/MemoryTitle'
import { MemoryToolBar } from 'components/content/memory/memoryToolBar/MemoryToolBar'
import MemoryView from 'components/content/memory/memoryViewer/MemoryView'
import { SBottomBar } from './MemoryViewer.styled'
import { RemainingAnimations } from 'components/content/memory/remainingAnimations/RemainingAnimations'
import { useFakeProgressBar } from 'components/fakeProgressBar/FakeProgressBar'
import { useFullScreenView } from 'components/fullScreenView/FullScreenView'
import { usePaywallMemory } from 'components/memory/MemoryPaywallUtils'
import {
  AnimationDriver,
  AnimationType,
  incompleteAnimationStatuses,
  livePortraitDrivers,
  SelectedMemory,
  useViewedMemory,
  ViewedMemory,
} from 'components/memory/MemoryUtils'
import { ConfirmModal } from 'components/modal/confirmModal/ConfirmModal'
import { useMemoryShareModal } from 'components/modal/memoryShareModal/MemoryShareModal'
import { useModal } from 'components/modal/Modal'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { fireEvent } from 'utils/analytics'
import { sh_finished } from 'utils/analytics/events/sh_finished'
import { AnimationPricing, AnimationSourcePage, SingingSourcePage } from 'utils/analytics/types'
import { getLastItem } from 'utils/array'
import { ApiError, MessageException } from 'utils/error'
import { useLoadingCallback, useRunOnceReady } from 'utils/hooks'
import { ApiType, GenericAsyncFunction } from 'utils/types'

type ViewMemoryParams = {
  viewedMemory: ViewedMemory
  driverToAnimateWith?: AnimationDriver | null
  source?: AnimationSourcePage | SingingSourcePage
}

type OngoingAnimation = {
  uuid?: string | null
  type?: AnimationType | null
}

export const useMemoryViewer = (
  allMemories: ViewedMemory[],
  onRefresh?: GenericAsyncFunction,
  disabled?: boolean
) => {
  const { t } = useTranslation('life_stories')
  const [ongoingAnimation, setOngoingAnimation] = useState<OngoingAnimation | null>(null)

  const {
    viewedMemory,
    setViewedMemory,
    clearViewedMemory,
    goToNextMemory,
    goToPrevMemory,
    muted,
    toggleMuted,
  } = useViewedMemory(allMemories)

  const { data, mutate } = useUserMemory(
    viewedMemory.memoryUuid,
    !!ongoingAnimation ? { refreshInterval: 4000 } : undefined
  )

  // View the original file or an animation of the memory based on viewedMemory, which is based on URL params
  const selectedMemory: SelectedMemory = useMemo(
    () => ({
      ...data?.json,
      selectedAnimation:
        data?.json?.animated_files?.find((a) => a.uuid === viewedMemory.animationUuid) || null,
    }),
    [data, viewedMemory.animationUuid]
  )

  // If this memory is being animated and the user is viewing the ongoing animation or the original image, set the ongoing animation to be observed
  useEffect(() => {
    const ongoingAnimations = selectedMemory?.animated_files?.filter((a) =>
      incompleteAnimationStatuses.includes(a.status)
    )
    let ongoingAnimationToObserve
    if (viewedMemory.animationUuid) {
      ongoingAnimationToObserve = ongoingAnimations?.find(
        (a) => a.uuid === viewedMemory.animationUuid
      )
    } else {
      ongoingAnimationToObserve = getLastItem(ongoingAnimations)
    }
    if (ongoingAnimationToObserve?.uuid) {
      setOngoingAnimation({
        uuid: ongoingAnimationToObserve.uuid,
        type: ongoingAnimationToObserve?.animation_type,
      })
    }

    return () => {
      setOngoingAnimation(null)
    }
  }, [selectedMemory, viewedMemory.memoryUuid, viewedMemory.animationUuid])

  const [shareModal, openShareModal] = useMemoryShareModal(selectedMemory)

  const [fakeProgressBar, progressFakeProgressBar] = useFakeProgressBar({
    style: { backgroundColor: 'gray', height: '6px', minHeight: '6px' },
    filledStyle: { backgroundColor: 'white' },
  })

  const { paywallMemory, hitMemoryPaywallWithRedirect, clearPaywallMemory } = usePaywallMemory()

  const [requestAnimation, isRequestingAnimation] = useLoadingCallback(
    useCallback(
      async (driver: AnimationDriver, forceId?: string) => {
        const memoryUuid = forceId ?? viewedMemory.memoryUuid
        if (!memoryUuid || !driver) {
          throw new MessageException('missing_memory')
        }
        setOngoingAnimation({
          type: livePortraitDrivers.includes(driver) ? 'LIVE_PORTRAIT' : 'SINGING_PORTRAIT',
        })
        progressFakeProgressBar(95, 15000)
        try {
          const resp = await animateUserMemory(memoryUuid, {
            driver_type: driver,
          })
          if (resp.ok) {
            await mutate(resp)
            setViewedMemory({
              memoryUuid: resp.json?.uuid,
              animationUuid: getLastItem(resp.json?.animated_files)?.uuid,
            })
          } else {
            if (resp.errorCode === 'user_exceeded_free_quota') {
              await progressFakeProgressBar(0, 500)
              await hitMemoryPaywallWithRedirect({
                memoryUuid: memoryUuid,
                driver,
              })
              return
            } else {
              throw new ApiError(resp)
            }
          }
        } catch (err) {
          await progressFakeProgressBar(0, 500)
          setOngoingAnimation(null)
          handleAndToastError(err)
        }
      },
      [
        hitMemoryPaywallWithRedirect,
        mutate,
        progressFakeProgressBar,
        setViewedMemory,
        viewedMemory.memoryUuid,
      ]
    )
  ) as [(type: AnimationDriver, forceId?: string | number) => Promise<unknown>, boolean, boolean]

  useEffect(() => {
    const checkStatus = async (ongoingAnimation: ApiType['MemoryFileResponse']) => {
      if (ongoingAnimation.status === 'DONE') {
        if (ongoingAnimation.animation_type === 'LIVE_PORTRAIT') {
          fireEvent('animation_finished', {
            pricing: ongoingAnimation.animation_source_type?.toLowerCase() as AnimationPricing,
            driver: ongoingAnimation.driver as string,
          })
        } else if (ongoingAnimation.animation_type === 'SINGING_PORTRAIT') {
          await sh_finished.fire(ongoingAnimation)
        }
        await setViewedMemory({ ...viewedMemory, animationUuid: ongoingAnimation.uuid })
        await progressFakeProgressBar(100, 1000)
        await onRefresh?.()
      } else if (ongoingAnimation.status === 'ERROR') {
        handleAndToastError(ongoingAnimation.error_msg)
        await progressFakeProgressBar(0, 1000)
        await setViewedMemory({ ...viewedMemory, animationUuid: null })
      }

      if (!incompleteAnimationStatuses.includes(ongoingAnimation.status)) {
        setOngoingAnimation(null)
        progressFakeProgressBar(0, 0)
      }
    }
    const upToDateAnimation = selectedMemory.animated_files?.find(
      (a) => a.uuid === ongoingAnimation?.uuid
    )
    if (upToDateAnimation) checkStatus(upToDateAnimation)
  }, [
    ongoingAnimation,
    progressFakeProgressBar,
    selectedMemory,
    setViewedMemory,
    viewedMemory,
    onRefresh,
  ])

  const [deleteModal, showDeleteModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        title={t('life_stories.inside_page.memory_viewer.delete_modal.title')}
        danger
        onConfirm={async () => {
          if (!viewedMemory.memoryUuid) return
          try {
            const resp = await deleteUserMemory(viewedMemory.memoryUuid)
            if (!resp.ok) throw Error(resp?.error?.toString?.())
            await onRefresh?.()
            toastSuccess('memory_deleted')
            close()
            clearViewedMemory()
          } catch (err) {
            handleAndToastError(err, 'failed_to_delete_memory')
          }
        }}
        close={close}
      />
    ) : null
  )

  const [memoryView, openMemoryView, closeMemoryView, isOpen] = useFullScreenView(
    ({ isOpen }) =>
      isOpen ? (
        <>
          {deleteModal}
          <MemoryView
            memory={selectedMemory}
            shareModal={shareModal}
            isAnimating={ongoingAnimation?.type || !!(ongoingAnimation || isRequestingAnimation)}
            progressBar={fakeProgressBar}
            onPrev={goToPrevMemory}
            onNext={goToNextMemory}
            muted={muted}
            toggleMuted={toggleMuted}
          />
        </>
      ) : null,
    {
      onClose: () => {
        clearViewedMemory()
      },
      disableFocusLock: true,
      topBarContent: (
        <MemoryToolBar
          onAnimate={(driver) => requestAnimation(driver)}
          isAnimating={!!(ongoingAnimation || isRequestingAnimation)}
          memory={selectedMemory}
          onDelete={showDeleteModal}
          onShare={openShareModal}
          setSelectedMemory={async (memory) =>
            await setViewedMemory({
              memoryUuid: memory.uuid,
              animationUuid: memory.selectedAnimation?.uuid,
            })
          }
        />
      ),
      bottomBarContent: (
        <SBottomBar>
          <RemainingAnimations />
          <MemoryTitle memoryUuid={selectedMemory?.uuid} css={{ marginRight: '12px' }} />
        </SBottomBar>
      ),
    }
  )

  const viewMemory = useCallback(
    async ({ viewedMemory, driverToAnimateWith }: ViewMemoryParams) => {
      await setViewedMemory(viewedMemory)
      if (driverToAnimateWith) {
        await requestAnimation(driverToAnimateWith, viewedMemory.memoryUuid)
      }
    },
    [requestAnimation, setViewedMemory]
  )

  /***************************************************/
  /************ Handle show detail flow **************/
  useEffect(() => {
    if (viewedMemory.memoryUuid && !isOpen && !disabled) {
      void openMemoryView()
    } else if (!viewedMemory.memoryUuid && isOpen) {
      closeMemoryView()
    }
  }, [closeMemoryView, isOpen, disabled, openMemoryView, viewedMemory.memoryUuid])
  /***************************************************/

  /***************************************************/
  /*********** Handle paywall after payment **********/
  const reserveData = useUserMemoryAnimationReserve(true).data?.json
  const count = getReserveTotal(reserveData)

  useRunOnceReady(!!paywallMemory?.memoryUuid && !!paywallMemory.driver && !!count, () => {
    if (paywallMemory?.memoryUuid && paywallMemory.driver && count) {
      viewMemory({
        viewedMemory: { memoryUuid: paywallMemory.memoryUuid },
        driverToAnimateWith: paywallMemory.driver,
      })
      clearPaywallMemory()
    }
  })
  /***************************************************/

  return [memoryView, viewMemory, closeMemoryView] as const
}
